import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Direction.css";

interface DirectionItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Direction: React.FC = () => {
  const [data, setData] = useState<DirectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/directions`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching directions:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: DirectionItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: DirectionItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Directions",
    de: "Richtungen",
    ta: "திசைகள்",
    si: "දිශා",
    hi: "दिशाएं",
    ar: "الاتجاهات",
    tr: "Yönler",
    ru: "Направления",
    zh: "方向",
    fr: "Directions",
    es: "Direcciones",
  };

  const subtitleMap = {
    en: "Directions in German",
    de: "Richtungen auf Deutsch",
    ta: "ஜெர்மனில் திசைகள்",
    si: "ජර්මානු භාෂාවෙන් දිශා",
    hi: "जर्मन में दिशाएं",
    ar: "الاتجاهات بالألمانية",
    tr: "Almanca'da Yönler",
    ru: "Направления на немецком",
    zh: "德语方向",
    fr: "Directions en allemand",
    es: "Direcciones en alemán",
  };

  return (
    <div className="directions-page-container">
      <div className="directions-page-header">
        <div>
          <h1 className="directions-page-title">{titleMap["de"]}</h1>
          <p className="directions-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="directions-page-loading">Loading...</div>
      ) : (
        <div className="directions-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`directions-page-card ${
                  isPlaying ? "directions-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="directions-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="directions-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="directions-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="directions-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="directions-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Direction;
